import { AuditLogSummary } from "@app/shared";
import { Icons, SortableTable, InfiniteScrollOptions, CardPosition } from "../../../shared";
import { FilterOption } from "../../../shared/Filter";
import { format } from "date-fns";
import { useCallback, useEffect, useState, useMemo } from "react";
import { AuditLogDetails } from "./AuditLogDetails";
import * as AuditLogses from "@app/shared/src/api/AuditLogsControllerClient.gen";
import { useDialog } from "../../../../hooks";
import { debounce, toastWarning } from "../../../../helpers";

type MappedAuditLogSummary = AuditLogSummary & { id: string };

type InfiniteScrollQuery = {
    limit: number;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<MappedAuditLogSummary>[];
    cursor?: string;
};

export const AuditLogsTable: React.FC = () => {
    const [auditLogs, setAuditLogs] = useState<AuditLogSummary[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | undefined>();

    const [query, setQuery] = useState<InfiniteScrollQuery>({
        limit: 50,
        sortKey: "timestamp",
        sortDirection: "desc",
    });

    const confirm = useDialog<AuditLogSummary>();

    const convertFiltersToParams = useCallback((filters?: FilterOption<MappedAuditLogSummary>[]) => {
        const params: {
            userId?: string;
            userName?: string;
            traceId?: string;
            dateFrom?: Date;
            dateTo?: Date;
            httpMethod?: string;
            controllerName?: string;
            actionName?: string;
        } = {};

        if (!filters) return params;

        filters.forEach((filter) => {
            if (filter.value !== undefined && filter.value !== "") {
                switch (filter.id) {
                    case "userName":
                        params.userName = filter.value;
                        break;
                    case "traceId":
                        params.traceId = filter.value;
                        break;
                    case "timestamp":
                        if (filter.value && typeof filter.value === "object") {
                            if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                            if (filter.value.to) params.dateTo = new Date(filter.value.to);
                        }
                        break;
                    case "httpMethod":
                        params.httpMethod = filter.value;
                        break;
                    case "controllerName":
                        params.controllerName = filter.value;
                        break;
                    case "actionName":
                        params.actionName = filter.value;
                        break;
                }
            }
        });

        return params;
    }, []);

    const fetchAuditLogs = useCallback(
        async (reset = false, cursor?: string) => {
            setIsLoading(true);
            try {
                const filterParams = convertFiltersToParams(query.filters);

                const response = await AuditLogses.getAuditLogs(
                    query.limit,
                    query.sortKey,
                    query.sortDirection,
                    filterParams.userId,
                    filterParams.userName,
                    filterParams.traceId,
                    filterParams.dateFrom,
                    filterParams.dateTo,
                    filterParams.httpMethod,
                    filterParams.controllerName,
                    filterParams.actionName,
                    cursor || query.cursor
                );

                if (reset) {
                    setAuditLogs(response?.data || []);
                } else {
                    setAuditLogs((prev) => {
                        // Prevent duplicates by checking IDs
                        const existingIds = new Set(prev.map((log) => log.id));
                        const newLogs = (response?.data || []).filter((log) => !existingIds.has(log.id));
                        return [...prev, ...newLogs];
                    });
                }

                setHasMore(response?.hasMore || false);
                setNextCursor(response?.nextCursor);
            } catch (error) {
                console.error("Failed to fetch audit logs:", error);
                toastWarning("Failed to load audit logs. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        },
        [query, convertFiltersToParams]
    );

    const debouncedFetchAuditLogs = useMemo(() => debounce(() => fetchAuditLogs(true), 300), [fetchAuditLogs]);

    useEffect(() => {
        debouncedFetchAuditLogs();
    }, [debouncedFetchAuditLogs]);

    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        setQuery((prev) => ({
            ...prev,
            sortKey,
            sortDirection: direction,
            cursor: undefined,
        }));
        setNextCursor(undefined);
        setHasMore(true);
        setAuditLogs([]);
    };

    const handleFilter = (filters: FilterOption<MappedAuditLogSummary>[]) => {
        setQuery((prev) => ({
            ...prev,
            filters,
            cursor: undefined,
        }));
        setNextCursor(undefined);
        setHasMore(true);
        setAuditLogs([]);
    };

    const handleLoadMore = useCallback(async () => {
        if (hasMore && !isLoading && nextCursor) {
            await fetchAuditLogs(false, nextCursor);
        }
    }, [hasMore, isLoading, nextCursor, fetchAuditLogs]);

    const infiniteScrollOptions: InfiniteScrollOptions = {
        enabled: true,
        onLoadMore: handleLoadMore,
        hasMore,
        isLoading,
        overscan: 1,
        followOutput: false,
    };

    const handleDetails = (auditLog: AuditLogSummary) => {
        confirm.show("Audit Log Details", "Close", auditLog, () => {});
    };

    const httpMethodFilterOptions = [
        { value: "POST", label: "POST" },
        { value: "PUT", label: "PUT" },
        { value: "DELETE", label: "DELETE" },
        { value: "PATCH", label: "PATCH" },
    ];

    return (
        <div className="p-6">
            <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Audit Logs</h2>
            </div>

            <SortableTable<MappedAuditLogSummary>
                mode="infiniteScroll"
                data={auditLogs?.map((log) => ({ ...log, id: log.id?.toString() || "" }))}
                isLoading={isLoading}
                className="w-full"
                rowClassNames="hover:bg-gray-50"
                filtering={true}
                onSort={handleSort}
                onFilter={handleFilter}
                sortKey={query.sortKey}
                sortDirection={query.sortDirection}
                infiniteScroll={infiniteScrollOptions}
                emptyValue="No audit logs found"
                columns={[
                    {
                        header: "Timestamp",
                        dataKey: "timestamp",
                        width: 70,
                        filterable: true,
                        filterType: "daterange",
                        renderFunc: (data) =>
                            data?.timestamp ? (
                                <div className="text-sm">
                                    <div>{format(new Date(data.timestamp), "MMM dd, yyyy")}</div>
                                    <div className="text-gray-500">{format(new Date(data.timestamp), "HH:mm:ss")}</div>
                                </div>
                            ) : null,
                    },
                    {
                        header: "Method",
                        dataKey: "httpMethod",
                        width: 50,
                        filterable: true,
                        filterType: "select",
                        filterOptions: httpMethodFilterOptions,
                        renderFunc: (data) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${data?.httpMethod === "PATCH" ? "bg-blue-100 text-blue-800" : data?.httpMethod === "POST" ? "bg-green-100 text-green-800" : data?.httpMethod === "PUT" ? "bg-yellow-100 text-yellow-800" : data?.httpMethod === "DELETE" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>{data?.httpMethod}</span>,
                    },
                    {
                        header: "Controller",
                        dataKey: "controllerName",
                        width: 60,
                        filterable: true,
                        filterType: "text",
                        renderFunc: (data) => <span className="text-sm font-medium text-gray-900">{data?.controllerName}</span>,
                    },
                    {
                        header: "Action",
                        dataKey: "actionName",
                        width: 100,
                        filterable: true,
                        filterType: "text",
                        renderFunc: (data) => <span className="text-sm text-gray-700">{data?.actionName}</span>,
                    },
                    {
                        header: "User",
                        dataKey: "userName",
                        width: 100,
                        filterable: true,
                        filterType: "text",
                        renderFunc: (data) => <span className="text-sm text-gray-700">{data?.userName || "Anonymous"}</span>,
                    },
                    {
                        header: "Status",
                        dataKey: "isSuccess",
                        width: 80,
                        renderFunc: (data) => (
                            <div className="flex items-center">
                                {data?.isSuccess ? (
                                    <div className="flex items-center text-green-600">
                                        <Icons.Success className="w-4 h-4 mr-1" />
                                        <span className="text-sm font-medium">{data.responseStatusCode}</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center text-red-600">
                                        <Icons.Close className="w-4 h-4 mr-1" />
                                        <span className="text-sm font-medium">{data?.responseStatusCode}</span>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        header: "Trace ID",
                        dataKey: "traceId",
                        width: 120,
                        filterable: true,
                        filterType: "text",
                        renderFunc: (data) => <span className="text-xs font-mono text-gray-600">{data?.traceId?.substring(0, 12)}...</span>,
                    },
                    {
                        header: "Actions",
                        dataKey: "id",
                        width: 80,
                        cardPosition: CardPosition.BottomRight,
                        renderFunc: (data) => (
                            <button onClick={() => data && handleDetails(data)} className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200 transition-colors">
                                Details
                            </button>
                        ),
                    },
                ]}
            />
            {confirm.renderDialog((auditLog) => {
                if (!auditLog) {
                    return <></>;
                }
                return <AuditLogDetails auditLog={auditLog} />;
            })}
        </div>
    );
};
